import React, { useMemo } from "react";
import { Modal } from "antd";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import { LandTourDetail, LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import { currencyDisplay } from "../../../../utils/helpers";
import { PaxCounts, PAX_TYPES, priceForPaxType } from "../components/ChooseYourTour";
import { passengerCategories } from "./bookingUtils";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { BookingQuote } from "./useBookingQuote";

interface Props {
  open: boolean;
  onClose: () => void;
  landTour: LandTourDetail;
  priceDate: LandTourPriceDate | undefined;
  paxCounts: PaxCounts;
  quote: BookingQuote | null;
  /** One entry per passenger, in PAX_TYPES order; blank counts as Malaysian (same as the quote). */
  nationalities?: string[];
  /** Credit/debit card admin fee on top of the total, when the card method is chosen. */
  adminFee?: number;
  ccRate?: number;
}

interface Line { label: string; amount: number }
interface Section { title: string; lines: Line[] }

const money = (value: number) => currencyDisplay("MYR", value);

// Mirrors LandTourBookings::PriceCalculator so each line is a real component of the charged amount:
//   customer price = category price (+ single supplement) + per-pax charges + non-Malaysian charge
// where infants skip per-pax and non-Malaysian charges, and a free-of-charge passenger skips the category
// price and per-pax charges. The quote total is what is charged, so whatever these lines don't explain
// (date/category surcharges) is shown as "Surcharges" and the sections always add up to the Grand Total.
export const buildBreakdown = ({ landTour, priceDate, paxCounts, quote, nationalities = [], adminFee = 0, ccRate = 0 }: Omit<Props, "open" | "onClose">): { sections: Section[]; total: number } => {
  const sections: Section[] = [];
  const passengers = passengerCategories(paxCounts);
  const adultPrice = priceForPaxType(priceDate, paxCounts, "adult");
  const categoryPrice = (key: keyof PaxCounts) => (key === "single_supplement" ? adultPrice : priceForPaxType(priceDate, paxCounts, key));
  // A single-room passenger pays the adult fare plus the supplement.
  const farePrice = (key: keyof PaxCounts) => categoryPrice(key) + (key === "single_supplement" ? priceForPaxType(priceDate, paxCounts, "single_supplement") : 0);

  const fare: Line[] = [];
  let fareTotal = 0;
  PAX_TYPES.forEach(({ key, label }) => {
    const count = paxCounts[key];
    if (!count) return;
    const amount = farePrice(key) * count;
    fareTotal += amount;
    fare.push({ label: `${key === "single_supplement" ? "Single Supplement" : label} x ${count}`, amount });
  });
  if (fare.length) sections.push({ title: "Fare Breakdown", lines: fare });

  const freeOfCharge = (quote?.rows || []).filter((row) => row.free_of_charge);
  let focTotal = 0;
  if (freeOfCharge.length) {
    const lines = PAX_TYPES.flatMap(({ key, label }) => {
      const count = freeOfCharge.filter((row) => row.category === key).length;
      if (!count) return [];
      const amount = categoryPrice(key) * count;
      focTotal += amount;
      return [{ label: `${label} x ${count}`, amount: -amount }];
    });
    if (lines.length) sections.push({ title: "Free of Charge", lines });
  }

  const foreigners = passengers.filter((key, index) => key !== "infant" && nationalities[index] && nationalities[index] !== "Malaysia").length;
  const nonMalaysian = foreigners * Number(landTour.nonMalaysianCharge || 0);
  if (nonMalaysian > 0) sections.push({ title: "Non-Malaysian Charges", lines: [{ label: `Pax x ${foreigners}`, amount: nonMalaysian }] });

  const chargedPax = passengers.filter((key) => key !== "infant").length - freeOfCharge.filter((row) => row.category !== "infant").length;
  const compulsory = (landTour.compulsoryCharges || [])
    .map((charge) => ({ label: charge.title, amount: Number(charge.price || 0) * (charge.type === "Pax" ? Math.max(chargedPax, 0) : 1) }))
    .filter((line) => line.amount > 0);
  const compulsoryTotal = compulsory.reduce((sum, line) => sum + line.amount, 0);
  if (compulsory.length) sections.push({ title: "Compulsory Charges", lines: compulsory });

  const explained = fareTotal - focTotal + nonMalaysian + compulsoryTotal;
  const total = quote?.total ?? explained;
  const surcharges = total - explained;
  if (quote && surcharges > 0.5) sections.push({ title: "Surcharges", lines: [{ label: "Surcharges applied", amount: surcharges }] });

  if (adminFee > 0) {
    sections.push({ title: "Admin Fee", lines: [{ label: `Credit/Debit card (${Number((ccRate * 100).toFixed(2))}%)`, amount: adminFee }] });
  }

  return { sections, total: total + adminFee };
};

export default function PriceBreakdownModal({ open, onClose, ...rest }: Props) {
  const isMobile = useBreakpoint();
  const { sections, total } = useMemo(() => buildBreakdown(rest), [rest.landTour, rest.priceDate, rest.paxCounts, rest.quote, rest.nationalities?.join("|"), rest.adminFee, rest.ccRate]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered={!isMobile}
      closable={false}
      width={isMobile ? "100%" : 580}
      wrapClassName={`gt-breakdown-modal ${isMobile ? "is-sheet" : ""}`}
      destroyOnClose
    >
      <div className="gt-breakdown__head">
        <h3>{isMobile ? "Price Summary" : "Price Breakdown"}</h3>
        <button type="button" onClick={onClose} aria-label="Close"><Icon path={mdiClose} size="22px" /></button>
      </div>

      {sections.map((section) => (
        <div className="gt-breakdown__section" key={section.title}>
          <h4>{section.title}</h4>
          {section.lines.map((line) => (
            <div className="gt-breakdown__line" key={line.label}>
              <span>{line.label}</span>
              <strong>{line.amount < 0 ? `- ${money(Math.abs(line.amount))}` : money(line.amount)}</strong>
            </div>
          ))}
        </div>
      ))}

      <div className="gt-breakdown__total">
        <span>Grand Total</span>
        <strong>{money(total)}</strong>
      </div>
    </Modal>
  );
}
